import React, { useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'

import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { CompanyUserActions } from '@galo-monitor/stores/companyUser'
import colors from '@galo-monitor/styles/colors'
import DataTable from '@galo-monitor/web-components/DataTable'
import DataTableIcons from '@galo-monitor/web-components/DataTable/icons'
import UserModal from '@galo-monitor/web-containers/modals/company/UserModal'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

const Users = ({ companyId }) => {
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { routes, getActionsBySlug } = useRoutes()

  const { data: users, mutate, isValidating } = useGaloMonitorApi(`/admin/companies/${ companyId }/users?active=1`)

  const userActions = useMemo(
    () => getActionsBySlug(routes.selectedCompany.slug),
    [getActionsBySlug, routes.selectedCompany.slug]
  )

  const columns = useMemo(
    () => [
      {
        title: t('name'),
        field: 'name'
      },
      {
        title: t('email'),
        field: 'email'
      },
      {
        title: t('profile', { howMany: 1 }),
        field: 'companyRolePosition'
      },
      {
        title: t('role', { howMany: 1 }),
        field: 'role'
      },
      {
        title: t('phone'),
        field: 'phone'
      }
    ],
    [t]
  )

  const onError = useCallback(
    err => snackbar.error(
      t(err?.response?.data?.message || 'unexpected error occurred'), {
        persist: false
      }),
    [snackbar, t]
  )

  const onAddClick = useCallback(
    () => {
      createDialog({
        id: 'create-company-user',
        Component: UserModal,
        props: {
          farmsEndpoint: `/admin/companies/${ companyId }/farms`,
          rolesEndpoint: `/admin/companies/${ companyId }/roles`,
          onSuccess: mutate
        }
      })
    },
    [companyId, createDialog, mutate]
  )

  const onEditClick = useCallback(
    (_, user) => {
      createDialog({
        id: 'edit-company-user',
        Component: UserModal,
        props: {
          user: {
            ...user,
            companyId
          },
          userDataEndpoint: `/admin/companies/${ companyId }/users/${ user.id }`,
          farmsEndpoint: `/admin/companies/${ companyId }/farms`,
          rolesEndpoint: `/admin/companies/${ companyId }/roles`,
          onSuccess: mutate
        }
      })
    },
    [companyId, createDialog, mutate]
  )

  const onSoftDeleteClick = useCallback(
    (_, user) => {
      const actionTitle = user.isActive ? {
        present: t('inactivate'),
        past: t('inactivated', { gender: 'female', howMany: 1 })
      } : {
        present: t('activate'),
        past: t('activated', { gender: 'female', howMany: 1 })
      }

      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to {action} the "{name}" {this}?', {
            gender: 'male',
            howMany: 1,
            this: t('user', { howMany: 1 }),
            action: actionTitle.present,
            name: user.name
          }),
          buttonTitle: actionTitle.present,
          buttonProps: {
            color: user.isActive ? 'error' : 'primary'
          },
          onConfirm: () => {
            dispatch(CompanyUserActions.toggleActivateCompanyUser(
              user,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('user', { howMany: 1 }),
                  action: toLower(actionTitle.past)
                }))
                mutate()
              },
              onError
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutate, onError, snackbar, t]
  )

  const onHardDeleteClick = useCallback(
    (_, user) => {
      createDialog({
        id: 'confirmation-company-user',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to delete the "{name}" {this}?', {
            gender: 'male',
            howMany: 1,
            this: t('user', { howMany: 1 }),
            name: user.name
          }),
          onConfirm: () => {
            dispatch(CompanyUserActions.removeCompanyUser(
              user,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('user', { howMany: 1 }),
                  action: toLower(t('removed', { gender: 'male', howMany: 1 }))
                }))
                mutate()
              },
              onError
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutate, onError, snackbar, t]
  )

  const actions = useMemo(
    () => {
      const datatableActions = []
      if (userActions.edit) {
        datatableActions.push(
          row => ({
            icon: DataTableIcons.Edit,
            tooltip: t('datagrid body edit tooltip'),
            onClick: onEditClick,
            disabled: isValidating,
            iconButtonProps: {
              size: 'small',
              ...(!row.isActive ? { style: { display: 'none' } } : {})
            },
            position: 'row'
          })
        )
      }

      if (userActions.delete) {
        datatableActions.push(
          row => ({
            icon: row.isActive
              ? DataTableIcons.Delete
              : () => <DataTableIcons.Reply style={ { color: colors.red } } />,
            tooltip:
              row.isActive
                ? t('datagrid body inactivate tooltip')
                : t('datagrid body activate tooltip'),
            onClick: onSoftDeleteClick,
            disabled: isValidating,
            iconButtonProps: {
              size: 'small'
            },
            position: 'row'
          }),
          row => ({
            icon: () => <DataTableIcons.DeleteForever style={ { color: colors.red } } />,
            tooltip: t('datagrid body delete everything tooltip'),
            onClick: onHardDeleteClick,
            disabled: isValidating,
            iconButtonProps: {
              size: 'small',
              ...(row.isActive ? { style: { display: 'none' } } : {})
            },
            position: 'row'
          })
        )
      }

      return datatableActions
    },
    [isValidating, onEditClick, onHardDeleteClick, onSoftDeleteClick, t, userActions]
  )

  return (
    <DataTable
      loading={ isValidating }
      data={ users ?? [] }
      columns={ columns }
      actions={ actions }
      onAddClick={ userActions.write && onAddClick }
      softDelete
    />
  )
}

Users.propTypes = {
  companyId: PropTypes.string.isRequired
}

export default Users
